import { useEffect, useRef } from 'preact/hooks'

import { CurrencyValueForm } from 'models/apps'
import { CurrencyMinimalAmount, CurrencyRatesPrecision } from 'models/settings'

import { formatCurrencyAmount } from 'apps-utils/validate-currency-input'
import { getDefaultBaseAmountForPair } from 'apps-utils/exchange-default-amount'

type UseDefaultBaseAmountProps = {
  baseCurrency: string
  counterCurrency: string
  exchangeRatePrecision: CurrencyRatesPrecision
  minimalAmounts: CurrencyMinimalAmount[]
  defaultBaseAmount: string
  value: CurrencyValueForm
  setValue: (value: CurrencyValueForm) => void
}

export const useDefaultBaseAmount = ({
  baseCurrency,
  counterCurrency,
  exchangeRatePrecision,
  minimalAmounts,
  defaultBaseAmount,
  value,
  setValue,
}: UseDefaultBaseAmountProps) => {
  const appliedDefaultAmount = useRef(defaultBaseAmount)

  const nextDefaultAmount = getDefaultBaseAmountForPair({
    baseCurrency,
    counterCurrency,
    exchangeRatePrecision,
    minimalAmounts,
    fallbackAmount: defaultBaseAmount,
  })
  const isCurrentDefaultValue = value.currentValue === appliedDefaultAmount.current
  const shouldUpdate = isCurrentDefaultValue && value.currentValue !== nextDefaultAmount
  const effectiveValue = shouldUpdate ? formatCurrencyAmount(nextDefaultAmount) : value

  useEffect(() => {
    if (shouldUpdate) {
      appliedDefaultAmount.current = nextDefaultAmount
      setValue(effectiveValue)
    }
  }, [baseCurrency, counterCurrency, exchangeRatePrecision, minimalAmounts, defaultBaseAmount, value.currentValue])

  return effectiveValue
}
